// Apex Business Intelligence — Round 5 & 6 (additive)
// New pages: DocumentsCenter, ScheduleDayView, EquipmentTracking, ContactsDedupe,
//            XactimateBoard, TimecardsPage, MaterialsPage, ServiceRequestsPage,
//            ControlCenter, MapTechPins.
// Header widgets: GlobalClockButton, NotificationBell.
// Job overlay: JobSideOverlay (Round 4 Notes/Jobs UI wiring).

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "./intelligence";
import { BackButton, JobSectionPercentages } from "./round12";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...auth(), ...(opts.headers || {}) } })
    .then(async r => { const data = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(data.detail || r.statusText), { data, status: r.status }); return data; });

const labelStyle = { fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text3)", marginBottom: 4, display: "block" };
const inputStyle = { padding: "8px 10px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)", fontSize: 12, fontFamily: "var(--dm)", width: "100%" };
const card = { background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, padding: 18, marginBottom: 14 };
const subhead = { fontFamily: "var(--rajdhani)", fontSize: 14, fontWeight: 700, marginBottom: 10, letterSpacing: 0.5 };
const tableHead = { fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "var(--text3)", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const tableCell = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const pill = (bg, text = "#fff") => ({ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, letterSpacing: 0.5, background: bg, color: text });

// ───────────────────────────────────────────────────────────────
// DOCUMENTS CENTER (R5)
// ───────────────────────────────────────────────────────────────
export function DocumentsCenter() {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (tag) qs.set("tag", tag);
    j(`/api/iv/r5/documents?${qs.toString()}`).then(d => setDocs(d.documents || [])).catch(() => {});
  }, [q, tag]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const payload = { files: [] };
    for (const f of files) {
      const data = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      payload.files.push({ name: f.name, mime: f.type, size: f.size, data_base64: data, tags: tag ? [tag] : [], category: "General" });
    }
    try {
      const r = await j("/api/iv/r5/documents/bulk-upload", { method: "POST", body: JSON.stringify(payload) });
      const ok = (r.results || []).filter(x => !x.error).length;
      const fail = (r.results || []).filter(x => x.error).length;
      toast(`Uploaded ${ok} file${ok === 1 ? "" : "s"}${fail ? `, ${fail} failed` : ""}`);
      load();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };

  const allTags = useMemo(() => Array.from(new Set(docs.flatMap(d => d.tags || []))), [docs]);

  return (
    <div data-testid="r5-documents-center" style={{ padding: 24 }}>
      <div style={card}>
        <div style={subhead}>Documents · Bulk Upload + Tagging</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input data-testid="doc-search" placeholder="Search by filename" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputStyle, maxWidth: 240 }} />
          <select data-testid="doc-tag-filter" value={tag} onChange={e => setTag(e.target.value)} style={{ ...inputStyle, maxWidth: 200 }}>
            <option value="">All tags</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="btn btn-primary" data-testid="doc-upload-btn" onClick={() => fileInput.current?.click()} disabled={busy} style={{ fontSize: 11 }}>
            {busy ? "Uploading…" : "Bulk Upload"}
          </button>
          <input ref={fileInput} type="file" multiple style={{ display: "none" }} onChange={e => onUpload(e.target.files)} data-testid="doc-file-input" />
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Files ({docs.length})</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={tableHead}>Name</th><th style={tableHead}>Category</th><th style={tableHead}>Tags</th><th style={tableHead}>Size</th><th style={tableHead}>Uploaded</th><th style={tableHead}></th></tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                <td style={tableCell}>{d.name}</td>
                <td style={tableCell}>{d.category}</td>
                <td style={tableCell}>{(d.tags || []).map(t => <span key={t} style={{ ...pill("var(--bg3)", "var(--text)"), marginRight: 4 }}>{t}</span>)}</td>
                <td style={tableCell}>{((d.size || 0) / 1024).toFixed(1)} KB</td>
                <td style={tableCell}>{(d.uploaded_at || "").slice(0, 19).replace("T", " ")}</td>
                <td style={tableCell}>
                  <button className="btn btn-ghost" style={{ fontSize: 10 }} onClick={async () => {
                    const tagsStr = window.prompt("Tags (comma-separated)", (d.tags || []).join(","));
                    if (tagsStr === null) return;
                    const tags = tagsStr.split(",").map(s => s.trim()).filter(Boolean);
                    try { await j(`/api/iv/r5/documents/${d.id}/tags`, { method: "PUT", body: JSON.stringify({ tags }) }); toast("Saved"); load(); } catch (e) { toast(e.message, "err"); }
                  }}>Tag</button>
                </td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={6} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No documents yet — drop files above to upload.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// SCHEDULE DAY VIEW (R5)
// ───────────────────────────────────────────────────────────────
export function ScheduleDayView() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState({ techs: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ tech: "", title: "", start: "", duration_min: 60, note: "" });
  // R13 — accept pre-fill via sessionStorage handoff (from Notes "schedule prompt → Yes")
  useEffect(() => {
    let raw = null;
    try { raw = sessionStorage.getItem("r13_schedule_prefill"); } catch {}
    if (!raw) return;
    let parsed; try { parsed = JSON.parse(raw); } catch { return; }
    if (!parsed || (!parsed.job_id && !parsed.subject && !parsed.description)) return;
    const jobId = parsed.job_id || "";
    const subject = parsed.subject || "";
    const description = parsed.description || "";
    setForm(f => ({
      ...f,
      title: subject ? `${jobId ? `[${jobId}] ` : ""}${subject}` : (jobId ? `[${jobId}] Follow-up` : f.title),
      note: description || (jobId ? `Linked to job ${jobId}` : f.note),
    }));
    setShowAdd(true);
    try { sessionStorage.removeItem("r13_schedule_prefill"); } catch {}
  }, []);

  const load = useCallback(() => {
    j(`/api/iv/r5/schedule/day?date=${date}`).then(setData).catch(() => {});
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.tech || !form.title || !form.start) return toast("Tech, title, start required", "err");
    try {
      await j("/api/iv/r5/schedule/assign", { method: "POST", body: JSON.stringify({ ...form, date }) });
      toast("Assignment added"); setShowAdd(false); setForm({ tech: "", title: "", start: "", duration_min: 60, note: "" }); load();
    } catch (e) { toast(e.message, "err"); }
  };

  return (
    <div data-testid="r5-schedule-day-view" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={subhead}>Schedule · Tech Day View</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} data-testid="schedule-date" />
            <button className="btn btn-primary" data-testid="schedule-add-btn" style={{ fontSize: 11 }} onClick={() => setShowAdd(true)}>+ Assign Slot</button>
          </div>
        </div>
      </div>
      {data.techs.length === 0 && <div style={{ ...card, color: "var(--text3)", textAlign: "center" }}>No assignments for {date}. Click "Assign Slot" above.</div>}
      {data.techs.map(t => (
        <div key={t.name} style={card} data-testid={`schedule-tech-${t.name}`}>
          <div style={{ ...subhead, color: "var(--cyan)" }}>{t.name} · {t.items.length} item{t.items.length === 1 ? "" : "s"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 10 }}>
            {t.items.map((it, i) => (
              <div key={i} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{it.title}</div>
                <div style={{ fontSize: 10, color: "var(--text3)" }}>{(it.start || "").slice(11, 16)} · {it.duration_min} min · {it.type}</div>
                {it.address && <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>{it.address}</div>}
                {it.status && <span style={pill("#1d4ed8")}>{it.status}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {showAdd && (
        <div data-testid="schedule-add-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000 }}>
          <div style={{ ...card, width: 460, marginBottom: 0 }}>
            <div style={subhead}>Assign Slot · {date}</div>
            <label style={labelStyle}>Tech</label><input style={inputStyle} value={form.tech} onChange={e => setForm({ ...form, tech: e.target.value })} data-testid="schedule-form-tech" />
            <label style={{ ...labelStyle, marginTop: 8 }}>Title</label><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="schedule-form-title" />
            <label style={{ ...labelStyle, marginTop: 8 }}>Start (ISO)</label><input style={inputStyle} placeholder={`${date}T08:00:00`} value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} data-testid="schedule-form-start" />
            <label style={{ ...labelStyle, marginTop: 8 }}>Duration (min)</label><input type="number" style={inputStyle} value={form.duration_min} onChange={e => setForm({ ...form, duration_min: +e.target.value })} data-testid="schedule-form-dur" />
            <label style={{ ...labelStyle, marginTop: 8 }}>Note</label><input style={inputStyle} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="schedule-form-note" />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={() => setShowAdd(false)} style={{ fontSize: 11 }}>Cancel</button>
              <button className="btn btn-primary" onClick={save} data-testid="schedule-form-save" style={{ fontSize: 11 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// EQUIPMENT TRACKING (R5)
// ───────────────────────────────────────────────────────────────
export function EquipmentTracking() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [maintenanceDue, setMaintenanceDue] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { asset_tag: "", name: "", category: "General", serial: "", status: "Available", location: "", purchase_date: "", next_maintenance: "", notes: "" };

  const load = useCallback(() => {
    const qs = new URLSearchParams(); if (q) qs.set("q", q); if (status) qs.set("status", status);
    j(`/api/iv/r5/equipment?${qs.toString()}`).then(d => setItems(d.equipment || [])).catch(() => {});
    j(`/api/iv/r5/equipment/maintenance/due?days=14`).then(d => setMaintenanceDue(d.due || [])).catch(() => {});
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit.asset_tag || !edit.name) return toast("Asset tag and name required", "err");
    try {
      if (edit.id) await j(`/api/iv/r5/equipment/${edit.id}`, { method: "PUT", body: JSON.stringify(edit) });
      else await j(`/api/iv/r5/equipment`, { method: "POST", body: JSON.stringify(edit) });
      toast("Saved"); setShowAdd(false); setEdit(null); load();
    } catch (e) { toast(e.message, "err"); }
  };

  const STATUSES = ["Available", "Deployed", "Maintenance", "Retired"];
  return (
    <div data-testid="r5-equipment-tracking" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={subhead}>Equipment Tracking</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input placeholder="Search tag/name/serial" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputStyle, width: 220 }} data-testid="eq-search" />
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 160 }} data-testid="eq-status-filter">
              <option value="">All statuses</option>{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="btn btn-primary" onClick={() => { setEdit({ ...blank }); setShowAdd(true); }} data-testid="eq-add-btn" style={{ fontSize: 11 }}>+ Add Asset</button>
          </div>
        </div>
      </div>
      {maintenanceDue.length > 0 && (
        <div style={{ ...card, borderColor: "#f59e0b" }} data-testid="eq-maintenance-banner">
          <div style={{ ...subhead, color: "#f59e0b" }}>Maintenance Due (next 14 days) · {maintenanceDue.length}</div>
          <div style={{ fontSize: 11 }}>{maintenanceDue.map(m => `${m.asset_tag} ${m.name}`).join(" · ")}</div>
        </div>
      )}
      <div style={card}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>Tag</th><th style={tableHead}>Name</th><th style={tableHead}>Category</th><th style={tableHead}>Status</th><th style={tableHead}>Location</th><th style={tableHead}>Next Maint.</th><th style={tableHead}></th></tr></thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} data-testid={`eq-row-${e.id}`}>
                <td style={tableCell}><strong>{e.asset_tag}</strong></td>
                <td style={tableCell}>{e.name}</td>
                <td style={tableCell}>{e.category}</td>
                <td style={tableCell}><span style={pill(e.status === "Available" ? "#16a34a" : e.status === "Deployed" ? "#1d4ed8" : e.status === "Maintenance" ? "#f59e0b" : "#6b7280")}>{e.status}</span></td>
                <td style={tableCell}>{e.location}</td>
                <td style={tableCell}>{e.next_maintenance || "—"}</td>
                <td style={tableCell}><button className="btn btn-ghost" onClick={() => { setEdit({ ...e }); setShowAdd(true); }} style={{ fontSize: 10 }}>Edit</button></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={7} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No equipment. Add your first asset.</td></tr>}
          </tbody>
        </table>
      </div>
      {showAdd && edit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000 }} data-testid="eq-add-modal">
          <div style={{ ...card, width: 520, marginBottom: 0, maxHeight: "85vh", overflow: "auto" }}>
            <div style={subhead}>{edit.id ? "Edit Asset" : "Add Asset"}</div>
            {[
              ["Asset Tag *", "asset_tag"], ["Name *", "name"], ["Category", "category"], ["Serial #", "serial"],
              ["Location", "location"], ["Purchase Date", "purchase_date"], ["Next Maintenance", "next_maintenance"], ["Notes", "notes"]
            ].map(([lbl, k]) => (
              <div key={k} style={{ marginBottom: 8 }}>
                <label style={labelStyle}>{lbl}</label>
                <input style={inputStyle} value={edit[k] || ""} onChange={e => setEdit({ ...edit, [k]: e.target.value })} data-testid={`eq-form-${k}`} />
              </div>
            ))}
            <label style={labelStyle}>Status</label>
            <select style={inputStyle} value={edit.status} onChange={e => setEdit({ ...edit, status: e.target.value })} data-testid="eq-form-status">
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={() => { setShowAdd(false); setEdit(null); }} style={{ fontSize: 11 }}>Cancel</button>
              <button className="btn btn-primary" onClick={save} data-testid="eq-form-save" style={{ fontSize: 11 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// CONTACTS DEDUPE (R5)
// ───────────────────────────────────────────────────────────────
export function ContactsDedupe() {
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); j("/api/iv/r5/contacts/duplicates").then(d => { setGroups(d.duplicate_groups || []); }).catch(() => {}).finally(() => setBusy(false)); };
  useEffect(() => { load(); }, []);

  const merge = async (group, primary) => {
    const dupes = group.candidates.filter(c => c.id !== primary.id).map(c => ({ id: c.id, source: c.source }));
    try {
      await j("/api/iv/r5/contacts/merge", { method: "POST", body: JSON.stringify({ primary_id: primary.id, primary_source: primary.source, duplicate_ids: dupes, merged_fields: {} }) });
      toast(`Merged ${dupes.length} into ${primary.name}`); load();
    } catch (e) { toast(e.message, "err"); }
  };

  return (
    <div data-testid="r5-contacts-dedupe" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={subhead}>Contacts · Duplicate Detection</div>
          <button className="btn btn-ghost" onClick={load} disabled={busy} style={{ fontSize: 11 }} data-testid="dedupe-refresh">↻ Re-scan</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{busy ? "Scanning…" : `${groups.length} duplicate group${groups.length === 1 ? "" : "s"} found across imported contacts + customers.`}</div>
      </div>
      {groups.map((g, i) => (
        <div key={i} style={card} data-testid={`dedupe-group-${i}`}>
          <div style={{ ...subhead, color: "#f59e0b" }}>Match on {g.match_on}: <span style={{ color: "var(--text)" }}>{g.value}</span></div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={tableHead}>Source</th><th style={tableHead}>Name</th><th style={tableHead}>Email</th><th style={tableHead}>Phone</th><th style={tableHead}></th></tr></thead>
            <tbody>
              {g.candidates.map(c => (
                <tr key={`${c.source}-${c.id}`}>
                  <td style={tableCell}>{c.source}</td><td style={tableCell}>{c.name}</td>
                  <td style={tableCell}>{c.email}</td><td style={tableCell}>{c.phone}</td>
                  <td style={tableCell}><button className="btn btn-primary" onClick={() => merge(g, c)} style={{ fontSize: 10 }} data-testid={`merge-into-${c.id}`}>Make Primary & Merge</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {!groups.length && !busy && <div style={{ ...card, color: "var(--text3)", textAlign: "center" }}>No duplicates found.</div>}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// XACTIMATE BOARD (R5)
// ───────────────────────────────────────────────────────────────
export function XactimateBoard() {
  const [board, setBoard] = useState({ counts: {}, items: [], statuses: [] });
  const load = () => j("/api/iv/r5/xactimate/board").then(setBoard).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="r5-xactimate-board" style={{ padding: 24 }}>
      <div style={card}>
        <div style={subhead}>Xactimate · Status Board</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10 }}>
          {board.statuses.map(s => (
            <div key={s} style={{ background: "var(--bg3)", padding: 12, borderRadius: 6, textAlign: "center", border: "1px solid var(--border)" }} data-testid={`xact-stat-${s}`}>
              <div style={{ fontSize: 10, color: "var(--text3)", letterSpacing: 1 }}>{s.toUpperCase()}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--cyan)", fontFamily: "var(--rajdhani)" }}>{board.counts[s] || 0}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Recent ({board.items.length})</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>Job</th><th style={tableHead}>Estimate</th><th style={tableHead}>Status</th><th style={tableHead}>Amount</th><th style={tableHead}>Updated</th></tr></thead>
          <tbody>
            {board.items.map(it => (
              <tr key={it.job_id}>
                <td style={tableCell}>{it.job_id}</td><td style={tableCell}>{it.estimate_id || "—"}</td>
                <td style={tableCell}><span style={pill("#1d4ed8")}>{it.status}</span></td>
                <td style={tableCell}>{it.amount ? `$${Number(it.amount).toLocaleString()}` : "—"}</td>
                <td style={tableCell}>{(it.updated_at || "").slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
            {!board.items.length && <tr><td colSpan={5} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No Xactimate records yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// TIMECARDS PAGE (R6)
// ───────────────────────────────────────────────────────────────
export function TimecardsPage() {
  const [active, setActive] = useState(null);
  const [list, setList] = useState([]);
  const reload = () => {
    j("/api/iv/r6/timecards/me/active").then(d => setActive(d.active)).catch(() => {});
    j("/api/iv/r6/timecards?limit=100").then(d => setList(d.timecards || [])).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  return (
    <div data-testid="r6-timecards-page" style={{ padding: 24 }}>
      <div style={card}>
        <div style={subhead}>Timecards</div>
        <div style={{ fontSize: 12 }}>{active ? <>Active since <strong>{(active.clock_in || "").slice(11, 19)}</strong> · {active.clock_in_gps ? `GPS: ${active.clock_in_gps.lat?.toFixed(4)}, ${active.clock_in_gps.lon?.toFixed(4)}` : "No GPS"}</> : "Not clocked in."}</div>
      </div>
      <div style={card}>
        <div style={subhead}>History</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>User</th><th style={tableHead}>Clock In</th><th style={tableHead}>Clock Out</th><th style={tableHead}>Duration</th><th style={tableHead}>Approval</th><th style={tableHead}></th></tr></thead>
          <tbody>
            {list.map(t => (
              <tr key={t.id} data-testid={`tc-row-${t.id}`}>
                <td style={tableCell}>{t.user_email}</td>
                <td style={tableCell}>{(t.clock_in || "").slice(0, 16).replace("T", " ")}</td>
                <td style={tableCell}>{t.clock_out ? (t.clock_out || "").slice(0, 16).replace("T", " ") : <span style={pill("#16a34a")}>ACTIVE</span>}</td>
                <td style={tableCell}>{t.duration_min ? `${Math.floor(t.duration_min / 60)}h ${t.duration_min % 60}m` : "—"}</td>
                <td style={tableCell}><span style={pill(t.approval_status === "approved" ? "#16a34a" : "#f59e0b")}>{t.approval_status}</span></td>
                <td style={tableCell}>{t.approval_status === "pending" && t.clock_out && (
                  <button className="btn btn-primary" style={{ fontSize: 10 }} onClick={async () => { try { await j(`/api/iv/r6/timecards/${t.id}/approve`, { method: "POST" }); toast("Approved"); reload(); } catch (e) { toast(e.message, "err"); } }} data-testid={`tc-approve-${t.id}`}>Approve</button>
                )}</td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={6} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No timecards yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// MATERIALS PAGE (R6)
// ───────────────────────────────────────────────────────────────
export function MaterialsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [edit, setEdit] = useState(null);
  // R13d — active jobs for the "Assigned To" dropdown
  const [activeJobs, setActiveJobs] = useState([]);
  const blank = { sku: "", name: "", unit: "ea", unit_cost: 0, quantity_on_hand: 0, reorder_at: 0, location: "", supplier: "", notes: "", assigned_job_id: "", not_job_related: false, purpose_description: "", receipts: [] };

  const load = useCallback(() => {
    const qs = new URLSearchParams(); if (q) qs.set("q", q); if (lowOnly) qs.set("low", "true");
    j(`/api/iv/r6/materials?${qs.toString()}`).then(d => setItems(d.materials || [])).catch(() => {});
  }, [q, lowOnly]);
  useEffect(() => { load(); }, [load]);
  // Fetch active jobs once when the Add modal opens
  useEffect(() => {
    if (!showAdd) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fs/jobs`, { headers: { Authorization: "Bearer " + (localStorage.getItem("apex_auth_token") || "") } })
      .then(r => r.ok ? r.json() : { jobs: [] }).then(d => setActiveJobs((d.jobs || []).filter(jx => (jx.status || "").toLowerCase() !== "completed" && (jx.status || "").toLowerCase() !== "cancelled")))
      .catch(() => setActiveJobs([]));
  }, [showAdd]);

  const save = async () => {
    if (!edit.sku || !edit.name) return toast("SKU and name required", "err");
    try {
      if (edit.id) await j(`/api/iv/r6/materials/${edit.id}`, { method: "PUT", body: JSON.stringify(edit) });
      else await j(`/api/iv/r6/materials`, { method: "POST", body: JSON.stringify(edit) });
      toast("Saved"); setShowAdd(false); setEdit(null); load();
    } catch (e) { toast(e.message, "err"); }
  };

  const consume = async (m) => {
    const job_id = window.prompt("Job ID to consume against?", "");
    if (!job_id) return;
    const q2 = +(window.prompt("Quantity to consume?", "1") || 0);
    if (!q2 || q2 <= 0) return;
    try { const r = await j("/api/iv/r6/materials/consume", { method: "POST", body: JSON.stringify({ material_id: m.id, job_id, quantity: q2 }) }); toast(`Consumed · new qty: ${r.new_quantity}`); load(); } catch (e) { toast(e.message, "err"); }
  };

  return (
    <div data-testid="r6-materials-page" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={subhead}>Materials</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input placeholder="Search SKU / name" value={q} onChange={e => setQ(e.target.value)} style={{ ...inputStyle, width: 220 }} data-testid="mat-search" />
            <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}><input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} data-testid="mat-low-only" /> Low stock only</label>
            <button className="btn btn-primary" onClick={() => { setEdit({ ...blank }); setShowAdd(true); }} data-testid="mat-add-btn" style={{ fontSize: 11 }}>+ Add Material</button>
          </div>
        </div>
      </div>
      <div style={card}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>SKU</th><th style={tableHead}>Name</th><th style={tableHead}>Unit</th><th style={tableHead}>On Hand</th><th style={tableHead}>Reorder At</th><th style={tableHead}>Cost</th><th style={tableHead}></th></tr></thead>
          <tbody>
            {items.map(m => {
              const low = (m.quantity_on_hand || 0) <= (m.reorder_at || 0);
              return (
                <tr key={m.id} data-testid={`mat-row-${m.id}`}>
                  <td style={tableCell}><strong>{m.sku}</strong></td>
                  <td style={tableCell}>{m.name}</td>
                  <td style={tableCell}>{m.unit}</td>
                  <td style={tableCell}><span style={low ? pill("#cc0000") : pill("#16a34a")}>{m.quantity_on_hand}</span></td>
                  <td style={tableCell}>{m.reorder_at}</td>
                  <td style={tableCell}>${(m.unit_cost || 0).toFixed(2)}</td>
                  <td style={tableCell}>
                    <button className="btn btn-ghost" onClick={() => { setEdit({ ...m }); setShowAdd(true); }} style={{ fontSize: 10, marginRight: 4 }}>Edit</button>
                    <button className="btn btn-primary" onClick={() => consume(m)} style={{ fontSize: 10 }} data-testid={`mat-consume-${m.id}`}>Consume</button>
                  </td>
                </tr>
              );
            })}
            {!items.length && <tr><td colSpan={7} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No materials yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showAdd && edit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000 }} data-testid="mat-add-modal">
          <div style={{ ...card, width: 560, marginBottom: 0, maxHeight: "92vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <button onClick={() => { setShowAdd(false); setEdit(null); }} data-testid="mat-back" style={{ padding: "4px 10px", fontSize: 11, color: "#2C2C2C", fontWeight: 600, background: "transparent", border: "1px solid #c8c8c8", borderRadius: 4, cursor: "pointer" }}>← Back</button>
              <div style={subhead}>{edit.id ? "Edit Material" : "Add Material"}</div>
            </div>
            {[["SKU *", "sku"], ["Name *", "name"], ["Unit", "unit"], ["Unit Cost", "unit_cost"], ["Quantity on Hand", "quantity_on_hand"], ["Reorder At", "reorder_at"], ["Location", "location"], ["Supplier", "supplier"]].map(([lbl, k]) => (
              <div key={k} style={{ marginBottom: 6 }}>
                <label style={{ ...labelStyle, color: "#1a1a1a", fontWeight: 600 }}>{lbl}</label>
                <input style={{ ...inputStyle, color: "#1a1a1a", background: "#fff" }} type={["unit_cost", "quantity_on_hand", "reorder_at"].includes(k) ? "number" : "text"} value={edit[k] ?? ""} onChange={e => setEdit({ ...edit, [k]: ["unit_cost", "quantity_on_hand", "reorder_at"].includes(k) ? +e.target.value : e.target.value })} data-testid={`mat-form-${k}`} />
              </div>
            ))}
            {/* R13d — Assigned To (job OR not-job-related) */}
            <div style={{ marginTop: 12, padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fafafa" }}>
              <label style={{ ...labelStyle, color: "#1a1a1a", fontWeight: 700, fontSize: 11 }}>Assigned To</label>
              {!edit.not_job_related && (
                <select style={{ ...inputStyle, color: "#1a1a1a", background: "#fff", marginTop: 4 }} value={edit.assigned_job_id || ""} onChange={e => setEdit({ ...edit, assigned_job_id: e.target.value })} data-testid="mat-form-assigned-job">
                  <option value="">— Select an active job —</option>
                  {activeJobs.map(jx => (<option key={jx.id} value={jx.id}>{jx.id} — {jx.customer}</option>))}
                </select>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11, color: "#1a1a1a", fontWeight: 600 }}>
                <input type="checkbox" checked={!!edit.not_job_related} onChange={e => setEdit({ ...edit, not_job_related: e.target.checked, assigned_job_id: e.target.checked ? "" : edit.assigned_job_id })} data-testid="mat-form-not-job-related" />
                Not job-related
              </label>
              {edit.not_job_related && (
                <div style={{ marginTop: 6 }}>
                  <label style={{ ...labelStyle, color: "#1a1a1a", fontWeight: 600 }}>Purpose / Description</label>
                  <input style={{ ...inputStyle, color: "#1a1a1a", background: "#fff" }} value={edit.purpose_description || ""} onChange={e => setEdit({ ...edit, purpose_description: e.target.value })} placeholder="e.g. Office stocking refill" data-testid="mat-form-purpose" />
                </div>
              )}
            </div>
            {/* R13d — Receipt upload (JPG/PNG/PDF, max 3) */}
            <div style={{ marginTop: 12, padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 6, background: "#fafafa" }}>
              <label style={{ ...labelStyle, color: "#1a1a1a", fontWeight: 700, fontSize: 11 }}>Receipt (up to 3 · JPG / PNG / PDF)</label>
              <input type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" multiple data-testid="mat-form-receipts" onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                const remaining = 3 - (edit.receipts || []).length;
                const accepted = files.slice(0, remaining);
                const next = [...(edit.receipts || [])];
                for (const f of accepted) {
                  const b64 = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(f); });
                  next.push({ filename: f.name, content_type: f.type, data_base64: b64 });
                }
                setEdit({ ...edit, receipts: next });
                e.target.value = "";
              }} disabled={(edit.receipts || []).length >= 3} style={{ display: "block", marginTop: 4, fontSize: 11, color: "#1a1a1a" }} />
              <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(edit.receipts || []).map((r, i) => {
                  const isPdf = (r.content_type || "").includes("pdf") || /\.pdf$/i.test(r.filename || "");
                  return (
                    <div key={i} data-testid={`mat-receipt-${i}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: 6, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", maxWidth: 110 }}>
                      {isPdf ? (
                        <div style={{ width: 80, height: 80, background: "#fee2e2", color: "#7f1d1d", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 }}>PDF</div>
                      ) : (
                        <img alt={r.filename} src={r.data_base64} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4 }} />
                      )}
                      <div style={{ fontSize: 9, color: "#1a1a1a", fontWeight: 600, textAlign: "center", wordBreak: "break-all", maxWidth: 100 }}>{r.filename}</div>
                      <button onClick={() => setEdit({ ...edit, receipts: edit.receipts.filter((_, x) => x !== i) })} data-testid={`mat-receipt-remove-${i}`} style={{ fontSize: 9, padding: "2px 6px", background: "transparent", border: "1px solid #C0392B", color: "#C0392B", fontWeight: 700, borderRadius: 3, cursor: "pointer" }}>Remove</button>
                    </div>
                  );
                })}
                {(edit.receipts || []).length === 0 && <div style={{ fontSize: 10, color: "#666", fontWeight: 600 }}>No receipts attached yet.</div>}
              </div>
            </div>
            <div style={{ marginTop: 6 }}>
              <label style={{ ...labelStyle, color: "#1a1a1a", fontWeight: 600 }}>Notes</label>
              <input style={{ ...inputStyle, color: "#1a1a1a", background: "#fff" }} value={edit.notes || ""} onChange={e => setEdit({ ...edit, notes: e.target.value })} data-testid="mat-form-notes" />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={() => { setShowAdd(false); setEdit(null); }} style={{ fontSize: 11, color: "#1a1a1a", fontWeight: 600 }}>Cancel</button>
              <button className="btn btn-primary" onClick={() => {
                // R13d — guard: require job OR purpose
                if (!edit.not_job_related && !edit.assigned_job_id) { toast("Pick a job or check Not job-related", "err"); return; }
                if (edit.not_job_related && !(edit.purpose_description || "").trim()) { toast("Purpose / Description required when Not job-related", "err"); return; }
                save();
              }} data-testid="mat-form-save" style={{ fontSize: 11 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// SERVICE REQUESTS PAGE (R6)
// ───────────────────────────────────────────────────────────────
export function ServiceRequestsPage() {
  const [data, setData] = useState({ requests: [], stages: [], priorities: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [stageFilter, setStageFilter] = useState("");
  const blank = { customer_name: "", customer_phone: "", customer_email: "", address: "", issue_type: "General", priority: "Normal", description: "", source: "Phone" };
  const [form, setForm] = useState(blank);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams(); if (stageFilter) qs.set("stage", stageFilter);
    j(`/api/iv/r6/service-requests?${qs.toString()}`).then(setData).catch(() => {});
  }, [stageFilter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.customer_name) return toast("Customer name required", "err");
    try { await j("/api/iv/r6/service-requests", { method: "POST", body: JSON.stringify(form) }); toast("Service request created"); setShowAdd(false); setForm(blank); load(); } catch (e) { toast(e.message, "err"); }
  };

  const updateStage = async (sr, stage) => {
    try { await j(`/api/iv/r6/service-requests/${sr.id}`, { method: "PUT", body: JSON.stringify({ stage }) }); toast(`→ ${stage}`); load(); } catch (e) { toast(e.message, "err"); }
  };

  return (
    <div data-testid="r6-service-requests-page" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={subhead}>Service Requests</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={{ ...inputStyle, width: 180 }} data-testid="sr-stage-filter">
              <option value="">All stages</option>{(data.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="sr-add-btn" style={{ fontSize: 11 }}>+ New Request</button>
          </div>
        </div>
      </div>
      <div style={card}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>ID</th><th style={tableHead}>Customer</th><th style={tableHead}>Issue</th><th style={tableHead}>Priority</th><th style={tableHead}>Stage</th><th style={tableHead}>Created</th><th style={tableHead}>Actions</th></tr></thead>
          <tbody>
            {(data.requests || []).map(sr => (
              <tr key={sr.id} data-testid={`sr-row-${sr.id}`}>
                <td style={tableCell}><strong>{sr.id.slice(0, 12)}</strong></td>
                <td style={tableCell}>{sr.customer_name}<div style={{ fontSize: 10, color: "var(--text3)" }}>{sr.customer_phone}</div></td>
                <td style={tableCell}>{sr.issue_type}</td>
                <td style={tableCell}><span style={pill(sr.priority === "Emergency" ? "#cc0000" : sr.priority === "High" ? "#f59e0b" : "#1d4ed8")}>{sr.priority}</span></td>
                <td style={tableCell}>
                  <select value={sr.stage} onChange={e => updateStage(sr, e.target.value)} style={{ ...inputStyle, padding: "4px 6px", fontSize: 11 }} data-testid={`sr-stage-${sr.id}`}>
                    {(data.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td style={tableCell}>{(sr.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td style={tableCell}>
                  <button className="btn btn-ghost" onClick={() => setEdit(sr)} style={{ fontSize: 10 }} data-testid={`sr-view-${sr.id}`}>View</button>
                </td>
              </tr>
            ))}
            {!(data.requests || []).length && <tr><td colSpan={7} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No service requests.</td></tr>}
          </tbody>
        </table>
      </div>
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000 }} data-testid="sr-add-modal">
          <div style={{ ...card, width: 520, marginBottom: 0, maxHeight: "85vh", overflow: "auto" }}>
            <div style={subhead}>New Service Request</div>
            {[["Customer Name *", "customer_name"], ["Phone", "customer_phone"], ["Email", "customer_email"], ["Address", "address"], ["Issue Type", "issue_type"], ["Description", "description"], ["Source", "source"]].map(([lbl, k]) => (
              <div key={k} style={{ marginBottom: 6 }}><label style={labelStyle}>{lbl}</label><input style={inputStyle} value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`sr-form-${k}`} /></div>
            ))}
            <label style={labelStyle}>Priority</label>
            <select style={inputStyle} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="sr-form-priority">
              {(data.priorities || ["Low", "Normal", "High", "Emergency"]).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={() => setShowAdd(false)} style={{ fontSize: 11 }}>Cancel</button>
              <button className="btn btn-primary" onClick={create} data-testid="sr-form-create" style={{ fontSize: 11 }}>Create</button>
            </div>
          </div>
        </div>
      )}
      {edit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 8000 }} data-testid="sr-view-modal">
          <div style={{ ...card, width: 560, marginBottom: 0, maxHeight: "85vh", overflow: "auto" }}>
            <div style={subhead}>{edit.id} · {edit.customer_name}</div>
            <div style={{ fontSize: 12, marginBottom: 8 }}><strong>Stage:</strong> {edit.stage}</div>
            <div style={{ fontSize: 12, marginBottom: 8 }}><strong>Issue:</strong> {edit.issue_type} · {edit.priority}</div>
            <div style={{ fontSize: 12, marginBottom: 8 }}><strong>Description:</strong> {edit.description || "—"}</div>
            <div style={{ ...subhead, fontSize: 12, marginTop: 12 }}>History</div>
            {(edit.history || []).map((h, i) => (
              <div key={i} style={{ fontSize: 11, padding: "4px 0", borderBottom: "1px dotted var(--border)" }}>{(h.at || "").slice(0, 16).replace("T", " ")} · {h.stage} · {h.by} {h.note && `· ${h.note}`}</div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={() => setEdit(null)} style={{ fontSize: 11 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// CONTROL CENTER (R6) — live ops snapshot
// ───────────────────────────────────────────────────────────────
export function ControlCenter() {
  const [snap, setSnap] = useState(null);
  const reload = () => j("/api/iv/r6/control-center/snapshot").then(setSnap).catch(() => {});
  useEffect(() => { reload(); const t = setInterval(reload, 60000); return () => clearInterval(t); }, []);

  if (!snap) return <div style={{ padding: 24, color: "var(--text3)" }}>Loading Control Center…</div>;
  const stat = (label, value, color = "var(--cyan)") => (
    <div style={{ background: "var(--bg3)", padding: 14, borderRadius: 6, border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 10, color: "var(--text3)", letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "var(--rajdhani)" }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="r6-control-center" style={{ padding: 24 }}>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={subhead}>Control Center · Live Operations</div>
          <div style={{ fontSize: 10, color: "var(--text3)" }}>Auto-refresh 60s · Last: {(snap.ts || "").slice(11, 19)}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginTop: 12 }}>
          {stat("Techs on Clock", snap.techs_on_clock, "#16a34a")}
          {stat("Jobs Today", snap.jobs_today)}
          {stat("Open Jobs", snap.open_jobs)}
          {stat("ARM Open", snap.arm_open_requests, "#1d4ed8")}
          {stat("Open SRs", snap.open_service_requests, "#f59e0b")}
          {stat("Low Stock", snap.low_stock_count, snap.low_stock_count > 0 ? "#cc0000" : "#16a34a")}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={card}>
          <div style={subhead}>Active Techs ({snap.active_techs.length})</div>
          {snap.active_techs.length === 0 && <div style={{ color: "var(--text3)", fontSize: 12 }}>Nobody clocked in.</div>}
          {snap.active_techs.map((t, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
              <strong>{t.user_email}</strong>
              <span style={{ color: "var(--text3)", marginLeft: 6 }}>since {(t.since || "").slice(11, 16)}</span>
              {t.gps && <span style={{ marginLeft: 8, fontSize: 10, color: "var(--cyan)" }}>GPS</span>}
            </div>
          ))}
        </div>
        <div style={card}>
          <div style={subhead}>Low Stock</div>
          {snap.low_stock_items.length === 0 && <div style={{ color: "var(--text3)", fontSize: 12 }}>All materials above reorder threshold.</div>}
          {snap.low_stock_items.map((m, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
              <strong>{m.name}</strong> <span style={{ color: "#cc0000" }}>· {m.qty} on hand</span> <span style={{ color: "var(--text3)" }}>(reorder at {m.reorder_at})</span>
            </div>
          ))}
        </div>
      </div>
      <div style={card}>
        <div style={subhead}>Recent Notifications</div>
        {(snap.recent_notifications || []).map(n => (
          <div key={n.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
            <span style={pill(n.severity === "warn" ? "#f59e0b" : n.severity === "err" ? "#cc0000" : "#1d4ed8")}>{n.kind}</span>
            <span style={{ marginLeft: 8 }}>{n.title}</span>
            <span style={{ float: "right", color: "var(--text3)", fontSize: 10 }}>{(n.ts || "").slice(11, 19)}</span>
          </div>
        ))}
        {!(snap.recent_notifications || []).length && <div style={{ color: "var(--text3)", fontSize: 12 }}>No notifications.</div>}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// MAP TECH PINS (R6)
// ───────────────────────────────────────────────────────────────
export function MapTechPins() {
  const [pins, setPins] = useState([]);
  const [team, setTeam] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    const load = () => j("/api/iv/r6/map/tech-pins").then(d => setPins(d.pins || [])).catch(() => {});
    load(); const t = setInterval(load, 60000); return () => clearInterval(t);
  }, []);
  useEffect(() => {
    j("/api/iv/team").then(d => setTeam(d.team || [])).catch(() => {});
  }, []);
  const phoneFor = (email) => {
    const m = (team || []).find(t => (t.email || "").toLowerCase() === (email || "").toLowerCase());
    return m?.phone || "";
  };
  const nameFor = (email) => {
    const m = (team || []).find(t => (t.email || "").toLowerCase() === (email || "").toLowerCase());
    return m?.full_name || email;
  };
  return (
    <div data-testid="r6-map-pins" style={{ padding: 24 }}>
      <div style={card}>
        <div style={subhead}>Live Tech Map · {pins.length} pin{pins.length === 1 ? "" : "s"}</div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8 }}>GPS coordinates captured at clock-in from active timecards. Click a row to open the profile drawer with Call / SMS / Profile.</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={tableHead}>Tech</th><th style={tableHead}>Lat</th><th style={tableHead}>Lon</th><th style={tableHead}>Since</th><th style={tableHead}>Job</th><th style={tableHead}></th></tr></thead>
          <tbody>
            {pins.map((p, i) => (
              <tr key={i} data-testid={`pin-row-${i}`} onClick={() => setSel(p)} style={{ cursor: "pointer" }}>
                <td style={tableCell}>{nameFor(p.user_email)}</td>
                <td style={tableCell}>{p.lat?.toFixed(5)}</td>
                <td style={tableCell}>{p.lon?.toFixed(5)}</td>
                <td style={tableCell}>{(p.since || "").slice(11, 16)}</td>
                <td style={tableCell}>{p.job_id || "—"}</td>
                <td style={tableCell}><button data-testid={`pin-open-${i}`} className="btn btn-ghost" style={{ fontSize: 10, padding: "2px 8px" }}>Open</button></td>
              </tr>
            ))}
            {!pins.length && <tr><td colSpan={6} style={{ ...tableCell, color: "var(--text3)", textAlign: "center" }}>No GPS-enabled techs currently clocked in.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="modal-overlay" onClick={() => setSel(null)}>
          <div className="modal" data-testid="map-pin-drawer" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header"><div style={{ fontFamily: "var(--rajdhani)", fontSize: 16, fontWeight: 700 }}>{nameFor(sel.user_email)}</div><button className="icon-btn" onClick={() => setSel(null)}>×</button></div>
            <div style={{ padding: 18, fontSize: 12 }}>
              <div><strong>Email:</strong> {sel.user_email}</div>
              <div><strong>Phone:</strong> {phoneFor(sel.user_email) || "—"}</div>
              <div><strong>GPS:</strong> {sel.lat?.toFixed(5)}, {sel.lon?.toFixed(5)}</div>
              <div><strong>Job:</strong> {sel.job_id || "—"}</div>
              <div><strong>Clocked in since:</strong> {(sel.since || "").slice(0, 16).replace("T", " ")}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                {phoneFor(sel.user_email) ? (
                  <>
                    <a data-testid="map-pin-call" href={`tel:${phoneFor(sel.user_email)}`}
                       className="btn btn-primary" style={{ fontSize: 11, textDecoration: "none", background: "#f8fafc" }}>📞 Call</a>
                    <a data-testid="map-pin-sms" href={`sms:${phoneFor(sel.user_email)}`}
                       className="btn btn-primary" style={{ fontSize: 11, textDecoration: "none" }}>💬 SMS</a>
                  </>
                ) : (
                  <span style={{ fontSize: 11, color: "var(--text3)" }}>No phone on file for this user.</span>
                )}
                <a data-testid="map-pin-profile" href="#" onClick={(e) => { e.preventDefault(); alert("Full team profile is available in Administration → Team and Access."); }}
                   className="btn btn-outline" style={{ fontSize: 11, textDecoration: "none" }}>View Full Profile</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// GLOBAL CLOCK IN/OUT button (header)
// ───────────────────────────────────────────────────────────────
export function GlobalClockButton() {
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    j("/api/iv/r6/timecards/me/active").then(d => setActive(d.active)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, [refresh]);

  const getGps = () => new Promise(res => {
    if (!navigator.geolocation) return res(null);
    navigator.geolocation.getCurrentPosition(
      p => res({ gps_lat: p.coords.latitude, gps_lon: p.coords.longitude, gps_accuracy: p.coords.accuracy }),
      () => res(null), { timeout: 5000, maximumAge: 60000 }
    );
  });

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const gps = await getGps();
      if (!active) {
        await j("/api/iv/r6/timecards/clock-in", { method: "POST", body: JSON.stringify(gps || {}) });
        toast(gps ? "Clocked in · GPS captured" : "Clocked in");
      } else {
        await j("/api/iv/r6/timecards/clock-out", { method: "POST", body: JSON.stringify(gps || {}) });
        toast("Clocked out");
      }
      refresh();
    } catch (e) { toast(e.message, "err"); } finally { setBusy(false); }
  };

  return (
    <button
      data-testid="global-clock-btn"
      className={active ? "btn btn-primary" : "btn btn-outline"}
      onClick={onClick} disabled={busy}
      style={{ fontSize: 11, background: active ? "#16a34a" : undefined, borderColor: active ? "#16a34a" : undefined }}
      title={active ? `Clocked in at ${(active.clock_in || "").slice(11, 16)}` : "Not clocked in"}
    >
      {busy ? "…" : active ? "● Clock Out" : "○ Clock In"}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────
// NOTIFICATION BELL (R6) — 60s polling
// ───────────────────────────────────────────────────────────────
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const refresh = useCallback(() => {
    j("/api/iv/r6/notifications?limit=20").then(setData).catch(() => {});
  }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, [refresh]);

  const markAllRead = async () => {
    try { await j("/api/iv/r6/notifications/mark-read", { method: "POST", body: JSON.stringify({ all: true }) }); refresh(); } catch (e) { toast(e.message, "err"); }
  };

  return (
    <div style={{ position: "relative" }}>
      <button data-testid="notification-bell" className="btn btn-ghost" onClick={() => setOpen(o => !o)} style={{ fontSize: 14, position: "relative", padding: "6px 10px" }}>
        🔔
        {data.unread > 0 && <span style={{ position: "absolute", top: 0, right: 0, background: "#cc0000", color: "#fff", borderRadius: 999, fontSize: 9, padding: "1px 5px", minWidth: 16, textAlign: "center" }} data-testid="bell-unread-count">{data.unread}</span>}
      </button>
      {open && (
        <div data-testid="bell-dropdown" style={{ position: "absolute", right: 0, top: "100%", marginTop: 6, width: 360, maxHeight: 400, overflow: "auto", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 6, zIndex: 7000, boxShadow: "0 8px 20px rgba(0,0,0,0.3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>
            <strong style={{ fontSize: 12 }}>Notifications ({data.unread} unread)</strong>
            <button className="btn btn-ghost" onClick={markAllRead} style={{ fontSize: 10 }} data-testid="bell-mark-all">Mark all read</button>
          </div>
          {(data.notifications || []).length === 0 && <div style={{ padding: 16, fontSize: 12, color: "var(--text3)", textAlign: "center" }}>No notifications.</div>}
          {(data.notifications || []).map(n => (
            <div key={n.id} style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", fontSize: 11, background: n.read ? "transparent" : "rgba(29,78,216,0.08)" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={pill(n.severity === "warn" ? "#f59e0b" : n.severity === "err" ? "#cc0000" : "#1d4ed8")}>{n.kind}</span>
                <span style={{ color: "var(--text3)", fontSize: 10 }}>{(n.ts || "").slice(11, 19)}</span>
              </div>
              <div style={{ marginTop: 4, fontWeight: 600 }}>{n.title}</div>
              {n.body && <div style={{ color: "var(--text3)", marginTop: 2 }}>{n.body}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────
export function JobSideOverlay({ jobId, onClose }) {
  const [history, setHistory] = useState({ history: [], stages: [] });
  const [meta, setMeta] = useState({ subject: "", body: "", taggedUser: "" });
  const [closeReasons, setCloseReasons] = useState([]);
  const [closes, setCloses] = useState([]);
  const [closeReason, setCloseReason] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  // Round 13 — multi-note list + team list + schedule prompt
  const [notesList, setNotesList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showSchedulePrompt, setShowSchedulePrompt] = useState(false);
  const [lastSavedNote, setLastSavedNote] = useState(null);
  const noteId = `note_${jobId}`;
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!jobId) return;
    j(`/api/iv/r4/jobs/stage-history/${jobId}`).then(setHistory).catch(() => setHistory({ history: [], stages: [] }));
    j("/api/iv/r4/close-reasons/options").then(d => setCloseReasons(d.reasons || [])).catch(() => {});
    j(`/api/iv/r4/close-reasons/${jobId}`).then(d => setCloses(d.entries || [])).catch(() => {});
    // R13 — load chronological notes + team list
    j(`/api/iv/r12/jobs/${jobId}/notes`).then(d => setNotesList(d.notes || [])).catch(() => setNotesList([]));
    j(`/api/iv/r12/team/active`).then(d => setTeamMembers(d.members || [])).catch(() => setTeamMembers([]));
  }, [jobId, noteId]);

  // ✅ FIX 3 — explicit Save Note posts a NEW note and clears the form
  const saveNote = async () => {
    if (!(meta.subject || "").trim() && !(meta.body || "").trim()) {
      toast("Note must have a subject or body", "err");
      return;
    }
    try {
      const payload = {
        job_id: jobId,
        subject: meta.subject || "",
        body: meta.body || "",
        tagged_users: meta.taggedUser ? [meta.taggedUser] : [],
      };
      const r = await j(`/api/iv/r12/jobs/notes`, { method: "POST", body: JSON.stringify(payload) });
      setLastSavedNote(r.note);
      toast("Note saved");
      // Refresh the list and clear the form
      const list = await j(`/api/iv/r12/jobs/${jobId}/notes`);
      setNotesList(list.notes || []);
      setMeta({ subject: "", body: "", taggedUser: "" });
      setShowSchedulePrompt(true);
    } catch (e) { toast(e.message || "Failed to save", "err"); }
  };
  const setField = (k, v) => { setMeta(prev => ({ ...prev, [k]: v })); };

  const changeStage = async (to) => {
    const from = history.history?.[history.history.length - 1]?.to_stage;
    try {
      await j("/api/iv/r4/jobs/stage-history", { method: "POST", body: JSON.stringify({ job_id: jobId, from_stage: from, to_stage: to, note: "" }) });
      toast(`Stage → ${to}`);
      const h = await j(`/api/iv/r4/jobs/stage-history/${jobId}`); setHistory(h);
    } catch (e) { toast(e.message, "err"); }
  };

  const submitClose = async () => {
    if (!closeReason) return toast("Pick a close reason", "err");
    try {
      await j("/api/iv/r4/close-reasons", { method: "POST", body: JSON.stringify({ job_id: jobId, reason: closeReason, notes: closeNotes }) });
      toast("Close reason recorded");
      const c = await j(`/api/iv/r4/close-reasons/${jobId}`); setCloses(c.entries || []);
      setCloseReason(""); setCloseNotes("");
    } catch (e) { toast(e.message, "err"); }
  };

  if (!jobId) return null;
  // Dark contrast styles (FIX 10) — explicit dark charcoal labels on light bg
  const lblDark = { fontSize: 11, fontWeight: 600, color: "#2C2C2C", marginBottom: 4, display: "block", letterSpacing: 0.3, textTransform: "none" };
  const inDark = { padding: "8px 10px", background: "#fff", border: "1px solid #c8c8c8", borderRadius: 4, color: "#2C2C2C", fontSize: 12, fontWeight: 500, fontFamily: "var(--dm)", width: "100%" };
  const subDark = { fontFamily: "var(--rajdhani)", fontSize: 14, fontWeight: 700, marginBottom: 10, marginTop: 16, letterSpacing: 0.5, color: "#2C2C2C" };
  return (
    <div data-testid="job-side-overlay" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 520, background: "#fff", borderLeft: "1px solid #d8d8d8", boxShadow: "-8px 0 24px rgba(0,0,0,0.18)", zIndex: 7500, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #d8d8d8", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f5f5f5" }}>
        <div style={{ fontFamily: "var(--rajdhani)", fontWeight: 700, fontSize: 14, color: "#2C2C2C" }}>Job · {jobId}</div>
        <button className="btn btn-ghost" onClick={onClose} data-testid="overlay-close" style={{ fontSize: 12, color: "#2C2C2C", fontWeight: 600 }}>✕</button>
      </div>
      <div style={{ overflow: "auto", padding: 16, flex: 1, background: "#fff" }}>
        <BackButton onBack={onClose} label="← Back to Jobs" testid="overlay-back-btn" />
        <JobSectionPercentages jobId={jobId} />
        <div style={subDark}>Stage Pipeline</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {(history.stages || []).map(s => {
            const current = history.history?.[history.history.length - 1]?.to_stage === s;
            return (
              <button key={s} className={current ? "btn btn-primary" : "btn btn-ghost"} style={{ fontSize: 10, padding: "4px 8px", color: current ? "#fff" : "#2C2C2C", fontWeight: 600 }} onClick={() => changeStage(s)} data-testid={`overlay-stage-${s}`}>{s}</button>
            );
          })}
        </div>
        <div style={subDark}>Stage History</div>
        <div style={{ marginBottom: 14 }}>
          {(history.history || []).map((h, i) => (
            <div key={i} style={{ fontSize: 11, padding: "4px 0", borderBottom: "1px dotted #ddd", color: "#2C2C2C", fontWeight: 500 }}>
              {(h.at || "").slice(0, 16).replace("T", " ")} · {h.from_stage || "—"} → <strong style={{ color: "#2C2C2C" }}>{h.to_stage}</strong> · {h.by_email}
            </div>
          ))}
          {!(history.history || []).length && <div style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>No stage changes yet.</div>}
        </div>

        {/* ── FIX 3 — Note entry form ── */}
        <div style={subDark}>Add Note</div>
        <label style={lblDark}>Subject</label>
        <input style={inDark} value={meta.subject} onChange={e => setField("subject", e.target.value)} data-testid="overlay-note-subject" placeholder="e.g. Follow-up call with adjuster" />
        <label style={{ ...lblDark, marginTop: 8 }}>Body</label>
        <textarea style={{ ...inDark, minHeight: 80, fontFamily: "inherit" }} value={meta.body} onChange={e => setField("body", e.target.value)} data-testid="overlay-note-body" placeholder="Type the note body here…" />
        <label style={{ ...lblDark, marginTop: 8 }}>Tag Team Member</label>
        <select style={inDark} value={meta.taggedUser} onChange={e => setField("taggedUser", e.target.value)} data-testid="overlay-tag-team">
          <option value="">— No tag —</option>
          {teamMembers.map(m => <option key={m.email} value={m.email}>{m.name} {m.role ? `(${m.role})` : ""}</option>)}
        </select>
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button className="btn btn-primary" data-testid="overlay-save-note" onClick={saveNote} style={{ fontSize: 11 }}>Save Note</button>
          {showSchedulePrompt && lastSavedNote && (
            <div data-testid="schedule-prompt" style={{ marginLeft: 12, background: "#fff8e1", border: "1px solid #f0c674", borderRadius: 4, padding: "6px 10px", display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#2C2C2C", fontWeight: 600 }}>
              Does this note require a schedule entry?
              <button className="btn btn-outline" data-testid="schedule-prompt-yes" style={{ fontSize: 10, padding: "3px 8px", color: "#2C2C2C", fontWeight: 600 }} onClick={() => {
                // R13 — handoff to ScheduleDayView via sessionStorage + custom nav event
                try {
                  sessionStorage.setItem("r13_schedule_prefill", JSON.stringify({
                    job_id: jobId,
                    subject: lastSavedNote.subject || "",
                    description: lastSavedNote.body || "",
                  }));
                } catch {}
                window.dispatchEvent(new CustomEvent("apex:navigate", { detail: { page: "iv-schedule-day" } }));
                setShowSchedulePrompt(false);
                if (typeof onClose === "function") onClose();
              }}>Yes</button>
              <button className="btn btn-ghost" data-testid="schedule-prompt-no" style={{ fontSize: 10, padding: "3px 8px", color: "#2C2C2C", fontWeight: 600 }} onClick={() => setShowSchedulePrompt(false)}>No Thanks</button>
            </div>
          )}
        </div>

        {/* Chronological list of saved notes */}
        <div style={{ ...subDark, marginTop: 18 }}>Notes Timeline ({notesList.length})</div>
        {notesList.length === 0 && <div style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>No notes yet. Use the form above to add the first.</div>}
        {notesList.map(n => (
          <div key={n.id} data-testid={`note-item-${n.id}`} style={{ background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 6, padding: "10px 12px", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#2C2C2C" }}>{n.subject || <em style={{ color: "#888" }}>(no subject)</em>}</div>
            {n.body && <div style={{ fontSize: 12, color: "#2C2C2C", fontWeight: 500, marginTop: 4, whiteSpace: "pre-wrap" }}>{n.body}</div>}
            <div style={{ fontSize: 10, color: "#555", fontWeight: 600, marginTop: 8, display: "flex", flexWrap: "wrap", gap: 10 }}>
              <span>📅 {(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <span>👤 By: <strong style={{ color: "#2C2C2C" }}>{n.created_by_name || n.created_by_email}</strong></span>
              {(n.tagged_users || []).length > 0 && <span>🏷️ Tagged: <strong style={{ color: "#1e40af" }}>{(n.tagged_users || []).join(", ")}</strong></span>}
            </div>
          </div>
        ))}

        <div style={{ ...subDark, marginTop: 14 }}>Close Reason</div>
        <select style={inDark} value={closeReason} onChange={e => setCloseReason(e.target.value)} data-testid="overlay-close-reason">
          <option value="">— pick —</option>
          {closeReasons.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input style={{ ...inDark, marginTop: 6 }} placeholder="Notes (optional)" value={closeNotes} onChange={e => setCloseNotes(e.target.value)} data-testid="overlay-close-notes" />
        <button className="btn btn-primary" onClick={submitClose} style={{ marginTop: 8, fontSize: 11 }} data-testid="overlay-close-submit">Record Close</button>
        {closes.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 10, color: "#555", fontWeight: 600, marginBottom: 4 }}>HISTORY</div>
            {closes.map(c => (
              <div key={c.id} style={{ fontSize: 11, padding: "4px 0", borderBottom: "1px dotted #ddd", color: "#2C2C2C", fontWeight: 500 }}>{(c.at || "").slice(0, 16).replace("T", " ")} · {c.reason} · {c.by_email}{c.notes && ` — ${c.notes}`}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
